import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { MdClose, MdArrowForward } from "react-icons/md";
import apiClient from "../config/apiClient";

export default function AISBoothModal({ isOpen, onClose }) {
  const initialFormData = {
    iam_a: "",
    name: "",
    email: "",
    phone: "",
    company_name: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    // Allow only numbers, plus sign, spaces, and hyphens
    const phoneValue = value.replace(/[^0-9+\s-]/g, '');
    
    setFormData((prev) => ({
      ...prev,
      [name]: phoneValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");

    try {
      const res = await apiClient.post("ais-form-fill-up", {
        iam_a: formData.iam_a,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.company_name,
      });

      // console.log("AIS form submitted:", res.data);
      setSubmitted(true);
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.iam_a?.[0] ||
        err?.message ||
        "Something went wrong. Please try again.";

      setError(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (submitted) {
      setSubmitted(false);
      setFormData(initialFormData);
    }

    setError("");
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative bg-[#0d1b2a] text-white rounded-2xl p-4 sm:p-5 w-full max-w-sm sm:max-w-md shadow-2xl border border-white/10 z-10"
          >
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
            >
              <MdClose size={22} />
            </button>

            {submitted ? (
              <div className="text-center py-6 sm:py-10">
                <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">🎉</div>

                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Your booth visit is confirmed</h3>

                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                  Thank you for sharing your details.
                </p>

                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                  Meet us at the Simplifyte booth during ICAI AIS 2026 on 26–27 June 2026 at Bharat Mandapam, Delhi.
                </p>

                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
                  We've sent the visit details to your email.
                </p>

                <div className="flex gap-2 sm:gap-3 justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      const eventTitle = "Simplifyte Booth Visit - ICAI AIS 2026";
                      const eventDetails = "Meet us at the Simplifyte booth during ICAI AI Innovation Summit 2026 at Bharat Mandapam, Delhi";
                      const startDate = "20260626T090000";
                      const endDate = "20260627T180000";
                      const location = "Bharat Mandapam, Delhi";
                      
                      const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(eventDetails)}&location=${encodeURIComponent(location)}`;
                      window.open(googleCalendarUrl, '_blank');
                    }}
                    className="px-3 py-2 sm:px-4 sm:py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors"
                  >
                    Add to Calendar
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-2 sm:px-4 sm:py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors border border-white"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 rounded-full px-2 sm:px-3 py-1 mb-3 sm:mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
                  <span className="text-[10px] sm:text-xs font-semibold tracking-widest uppercase">
                    Technology Partner · AIS 2026
                  </span>
                </div>

                <h2 className="text-xl sm:text-2xl font-bold mb-2">
                 Meet Simplifyte at AIS 2026
                </h2>

                <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed">
                  We’ll be at ICAI AI Innovation Summit 2026, Bharat Mandapam, Delhi, on 26–27 June.
Share your details and drop by our booth to connect with the team and collect your exclusive summit gift.
                </p>

                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-1.5">
                      I am a... <span className="text-red-400">*</span>
                    </label>

                    <div className="relative">
                      <select
                        name="iam_a"
                        value={formData.iam_a}
                        onChange={handleChange}
                        required
                        className="w-full bg-[#1a2b3c] border border-gray-600/50 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 text-white text-xs sm:text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 appearance-none cursor-pointer"
                      >
                        <option value="" disabled hidden>
                          Choose one...
                        </option>
                        <option value="Chartered Accountant">Chartered Accountant</option>
                        <option value="CA Firm Owner / Partner">CA Firm Owner / Partner</option>
                        <option value="Advocate / Legal Professional">Advocate / Legal Professional</option>
                        <option value="IT Services / Consultant">IT Services / Consultant</option>
                        <option value="Business Owner">Business Owner</option>
                        <option value="Student / Article Assistant">Student / Article Assistant</option>
                        <option value="Other">Other</option>
                      </select>

                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">
                        ▾
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-1.5">
                      Name <span className="text-red-400">*</span>
                    </label>

                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="w-full bg-[#1a2b3c] border border-gray-600/50 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 text-white text-xs sm:text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-1.5">
                      Email / Work Email <span className="text-red-400">*</span>
                    </label>

                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      className="w-full bg-[#1a2b3c] border border-gray-600/50 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 text-white text-xs sm:text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-1.5">
                      Mobile / WhatsApp
                    </label>

                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="+91 98765 43210"
                      className="w-full bg-[#1a2b3c] border border-gray-600/50 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 text-white text-xs sm:text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-1.5">
                      Firm / Company Name
                    </label>

                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder="ABC & Co."
                      className="w-full bg-[#1a2b3c] border border-gray-600/50 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 text-white text-xs sm:text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  {error && (
                    <p className="text-red-400 text-xs text-center bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 sm:py-3.5 px-4 sm:px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/25 text-xs sm:text-sm"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Confirm Booth Visit <MdArrowForward size={18} />
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-gray-500 leading-relaxed">
                    By submitting, you agree to receive event updates from Simplifyte. No spam. You can opt out anytime.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
